import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  Flag,
  Loader2,
  MessageSquare,
  PackageCheck,
  Send,
  ShieldAlert,
  Truck,
  XCircle,
} from 'lucide-react';
import { useDocumentTitle, useSectionTranslation } from '@/hooks';
import { useLocale } from '@/lib/i18n';
import { api, isApiError, type ApiResult } from '@/lib/api';
import { formatPrice } from '@/lib/formatPrice';
import { formatDisplayPrice, useCurrency } from '@/lib/currency';
import { useAuth } from '@/lib/auth/AuthContext';
import { dashboardTranslations } from './translations';

interface OrderDelivery {
  id: string;
  method: string;
  status: string;
  details: { deliveredContent?: unknown } | null;
  deliveredAt: string | null;
}

interface OrderDispute {
  id?: string;
  status?: string;
  reason?: string;
  description?: string;
  sellerResponseDeadline?: string | null;
}

interface Order {
  id: string;
  offerId: string;
  productId: string;
  buyerId: string;
  sellerId: string;
  quantity: number;
  unitPrice: string;
  totalPrice: string;
  currency: string;
  status: string;
  createdAt: string;
  platformFee?: string;
  autoReleaseAt?: string | null;
  releasedAt?: string | null;
  withdrawableAt?: string | null;
  delivery: OrderDelivery | null;
  dispute?: OrderDispute | null;
  product: { id: string; name: string; slug: string; image: string | null };
  buyer: { userId: string; username: string; displayName: string };
  seller: { userId: string; username: string; displayName: string };
}

interface OrderMessage {
  id: string;
  orderId: string;
  senderId: string;
  body: string;
  createdAt: string;
  sender: { userId: string; username: string; displayName: string };
}

function extractDeliveredContent(details: OrderDelivery['details']): string[] {
  const raw = details?.deliveredContent;
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object' && 'deliveredContent' in (parsed as Record<string, unknown>)) {
        const inner = (parsed as Record<string, unknown>).deliveredContent;
        if (Array.isArray(inner)) return inner.map(String);
        return [String(inner)];
      }
      return [raw];
    } catch {
      return [raw];
    }
  }
  return [String(raw)];
}

const DISPUTE_REASONS = [
  'not-delivered',
  'not-as-described',
  'incomplete',
  'seller-not-responding',
  'technical',
  'duplicate',
  'other',
] as const;

const REPORT_REASONS = [
  'not-delivered',
  'wrong-item',
  'account-problem',
  'other',
] as const;

interface CountdownUnits {
  minutes: string;
  hoursMinutes: string;
  daysHours: string;
}

function formatCountdown(target: string, units: CountdownUnits, numberFormatter: Intl.NumberFormat): string {
  const ms = new Date(target).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return units.minutes.replace('{m}', numberFormatter.format(0));
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return units.daysHours.replace('{d}', numberFormatter.format(days)).replace('{h}', numberFormatter.format(hours));
  if (hours > 0) return units.hoursMinutes.replace('{h}', numberFormatter.format(hours)).replace('{m}', numberFormatter.format(minutes));
  return units.minutes.replace('{m}', numberFormatter.format(minutes));
}

export function OrdersPage() {
  const t = useSectionTranslation(dashboardTranslations);
  const op = t.ordersPage;
  const { locale } = useLocale();
  const { user, loading: authLoading } = useAuth();
  useDocumentTitle(op.title);
  const { currency: displayCurrency, rates } = useCurrency();

  const orderConverted = (order: Order): string | null => {
    const from = (order.currency || 'USDT').toUpperCase();
    if (from === displayCurrency.toUpperCase()) return null;
    return formatDisplayPrice({ amount: order.totalPrice, from, to: displayCurrency }, rates, locale)
      .converted;
  };

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deliverId, setDeliverId] = useState<string | null>(null);
  const [deliverText, setDeliverText] = useState('');
  const [messagesId, setMessagesId] = useState<string | null>(null);
  const [messages, setMessages] = useState<OrderMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [messagesError, setMessagesError] = useState<string | null>(null);

  const [disputeId, setDisputeId] = useState<string | null>(null);
  const [disputeReason, setDisputeReason] = useState<string>('not-delivered');
  const [disputeDescription, setDisputeDescription] = useState('');
  const [respondId, setRespondId] = useState<string | null>(null);
  const [respondText, setRespondText] = useState('');
  const [evidenceId, setEvidenceId] = useState<string | null>(null);
  const [evidenceNote, setEvidenceNote] = useState('');

  const [reportId, setReportId] = useState<string | null>(null);
  const [reportReason, setReportReason] = useState<string>('not-delivered');
  const [reportDetails, setReportDetails] = useState('');
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportBusy, setReportBusy] = useState(false);
  const [reportSubmitted, setReportSubmitted] = useState<{ orderId: string; ticketId: string } | null>(null);

  const [roleFilter, setRoleFilter] = useState<'all' | 'buyer' | 'seller'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'PAID' | 'DELIVERED' | 'CANCELLED' | 'DISPUTED'>('all');

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }),
    [locale],
  );

  const countdownUnits = useMemo(
    () => ({
      minutes: op.timeMinutesShort,
      hoursMinutes: op.timeHoursMinutes,
      daysHours: op.timeDaysHours,
    }),
    [op.timeMinutesShort, op.timeHoursMinutes, op.timeDaysHours],
  );

  const countdownFormatter = useMemo(() => new Intl.NumberFormat(locale), [locale]);

  const fetchOrders = useCallback(async () => {
    const res = await api.get<Order[]>('/orders');
    if (isApiError(res)) {
      setError(res.error?.code === 'NETWORK_ERROR' ? op.errors.network : op.errors.generic);
    } else {
      setOrders(res.data ?? []);
    }
    setLoading(false);
  }, [op.errors.network, op.errors.generic]);

  useEffect(() => {
    if (!user) return;
    fetchOrders();
  }, [user, fetchOrders]);

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      if (roleFilter === 'buyer' && order.buyerId !== user?.userId) return false;
      if (roleFilter === 'seller' && order.sellerId !== user?.userId) return false;
      if (statusFilter !== 'all') {
        const deliveryStatus = order.delivery?.status ?? 'PENDING';
        if (statusFilter === 'DELIVERED') {
          if (!(order.status === 'COMPLETED' || deliveryStatus === 'DELIVERED' || deliveryStatus === 'CONFIRMED')) return false;
        } else if (order.status !== statusFilter) {
          return false;
        }
      }
      return true;
    });
  }, [orders, roleFilter, statusFilter, user?.userId]);

  const runAction = useCallback(
    async (orderId: string, action: () => Promise<ApiResult<unknown>>) => {
      setBusyId(orderId);
      setError(null);
      try {
        const res = await action();
        if (isApiError(res)) {
          setError(res.error?.message || op.errors.action);
          return;
        }
        await fetchOrders();
      } catch {
        setError(op.errors.action);
      } finally {
        setBusyId(null);
      }
    },
    [fetchOrders, op.errors.action],
  );

  const handleConfirm = (orderId: string) =>
    runAction(orderId, () => api.post(`/orders/${orderId}/confirm`));

  const handleCancel = (orderId: string) =>
    runAction(orderId, () => api.post(`/orders/${orderId}/cancel`));

  const handleSubmitDispute = async (orderId: string) => {
    if (!disputeReason || !disputeDescription.trim()) return;
    await runAction(orderId, () =>
      api.post(`/orders/${orderId}/dispute`, {
        reason: disputeReason,
        description: disputeDescription.trim(),
      }),
    );
    setDisputeId(null);
    setDisputeReason('not-delivered');
    setDisputeDescription('');
  };

  const handleSubmitReport = async (orderId: string) => {
    if (!reportReason) return;
    setReportBusy(true);
    setReportError(null);
    try {
      const res = await api.post<{ ticketId: string }>(`/orders/${orderId}/report`, {
        reason: reportReason,
        ...(reportDetails.trim() ? { details: reportDetails.trim() } : {}),
      });
      if (isApiError(res)) {
        setReportError(res.error?.message || op.report.errors.submit);
        return;
      }
      setReportSubmitted({ orderId, ticketId: res.data?.ticketId ?? '' });
      setReportId(null);
      setReportReason('not-delivered');
      setReportDetails('');
    } catch {
      setReportError(op.report.errors.submit);
    } finally {
      setReportBusy(false);
    }
  };

  const handleRespond = async (orderId: string) => {
    if (!respondText.trim()) return;
    await runAction(orderId, () =>
      api.post(`/orders/${orderId}/dispute/respond`, { response: respondText.trim() }),
    );
    setRespondId(null);
    setRespondText('');
  };

  const handleEvidence = async (orderId: string) => {
    if (!evidenceNote.trim()) return;
    await runAction(orderId, () =>
      api.post(`/orders/${orderId}/dispute/evidence`, { note: evidenceNote.trim() }),
    );
    setEvidenceId(null);
    setEvidenceNote('');
  };

  const handleDeliver = async (orderId: string) => {
    if (!deliverText.trim()) return;
    await runAction(orderId, () =>
      api.post(`/orders/${orderId}/deliver`, { details: { deliveredContent: deliverText.trim() } }),
    );
    setDeliverId(null);
    setDeliverText('');
  };

  const fetchMessages = useCallback(
    async (orderId: string) => {
      setMessagesLoading(true);
      setMessagesError(null);
      const res = await api.get<OrderMessage[]>(`/orders/${orderId}/messages`);
      if (isApiError(res)) {
        setMessages([]);
        setMessagesError(res.error?.code === 'NETWORK_ERROR' ? op.errors.network : op.errors.generic);
      } else {
        setMessages(res.data ?? []);
      }
      setMessagesLoading(false);
    },
    [op.errors.network, op.errors.generic],
  );

  const toggleMessages = (orderId: string) => {
    if (messagesId === orderId) {
      setMessagesId(null);
      setMessages([]);
      setMessageText('');
      setMessagesError(null);
    } else {
      setMessagesId(orderId);
      setMessages([]);
      setMessageText('');
      setMessagesError(null);
      fetchMessages(orderId);
    }
  };

  const handleSendMessage = async (orderId: string) => {
    const body = messageText.trim();
    if (!body) return;
    setMessagesError(null);
    const res = await api.post<OrderMessage>(`/orders/${orderId}/messages`, { body });
    if (isApiError(res)) {
      setMessagesError(res.error?.code === 'NETWORK_ERROR' ? op.errors.network : op.errors.action);
      return;
    }
    setMessageText('');
    if (res.data) {
      setMessages((prev) => [...prev, res.data as OrderMessage]);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-accent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/auth/signin?from=${encodeURIComponent(window.location.pathname)}`} replace />;
  }

  return (
    <div className="space-y-8">
      <div className="max-w-3xl">
        <div className="flex items-center gap-2 mb-2">
          <PackageCheck size={24} className="text-accent" />
          <h1 className="font-display text-display-md text-brand-white">{op.heading}</h1>
        </div>
        <p className="text-body-md text-brand-muted">{op.description}</p>
      </div>

      {error && (
        <div
          className="flex items-center gap-2 rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-3 py-2.5 text-sm text-brand-danger"
          role="alert"
        >
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {orders.length > 0 && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label={op.filters.roleLabel}>
            {([
              { value: 'all', label: op.filters.all },
              { value: 'buyer', label: op.roles.buyer },
              { value: 'seller', label: op.roles.seller },
            ] as const).map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setRoleFilter(tab.value)}
                aria-pressed={roleFilter === tab.value}
                className={`h-8 px-3 rounded-lg border text-xs font-medium transition-colors ${
                  roleFilter === tab.value
                    ? 'border-accent/40 bg-accent/10 text-accent'
                    : 'border-surface-border text-brand-muted hover:text-brand-white hover:border-accent/30'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label={op.filters.statusLabel}>
            {([
              { value: 'all', label: op.filters.statusAll },
              { value: 'PAID', label: op.filters.paid },
              { value: 'DELIVERED', label: op.filters.delivered },
              { value: 'CANCELLED', label: op.filters.cancelled },
              { value: 'DISPUTED', label: op.filters.disputed },
            ] as const).map((chip) => (
              <button
                key={chip.value}
                type="button"
                onClick={() => setStatusFilter(chip.value)}
                aria-pressed={statusFilter === chip.value}
                className={`h-7 px-3 rounded-full border text-xs transition-colors ${
                  statusFilter === chip.value
                    ? 'border-accent/40 bg-accent/10 text-accent'
                    : 'border-surface-border text-brand-muted hover:text-brand-white hover:border-accent/30'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {orders.length === 0 ? (
        <p className="text-sm text-brand-muted text-center py-16 rounded-2xl bg-surface-card/40 border border-surface-border">
          {op.empty}
        </p>
      ) : filteredOrders.length === 0 ? (
        <p className="text-sm text-brand-muted text-center py-16 rounded-2xl bg-surface-card/40 border border-surface-border">
          {op.emptyFiltered}
        </p>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => {
            const isBuyer = order.buyerId === user.userId;
            const isSeller = order.sellerId === user.userId;
            const isBusy = busyId === order.id;
            const deliveryStatus = order.delivery?.status ?? 'PENDING';
            const statusKey = order.status as keyof typeof op.statuses;
            const deliveryKey = deliveryStatus as keyof typeof op.deliveryStatuses;
            const isDelivered = order.status === 'COMPLETED' || deliveryStatus === 'DELIVERED' || deliveryStatus === 'CONFIRMED';
            const deliveredContent = isBuyer && isDelivered ? extractDeliveredContent(order.delivery?.details ?? null) : [];

            const canConfirm = isBuyer && order.status === 'PAID' && deliveryStatus === 'DELIVERED';
            const canCancel = order.status === 'PAID';
            const canDispute = isBuyer && order.status === 'PAID';
            const canDeliver = isSeller && order.status === 'PAID' && (deliveryStatus === 'SELLER_ACTION_REQUIRED' || deliveryStatus === 'DELIVERING');
            const canRespond = isSeller && order.status === 'DISPUTED';
            const canAddEvidence = order.status === 'DISPUTED';
            const converted = orderConverted(order);

            return (
              <div key={order.id} className="p-5 rounded-2xl bg-surface-card border border-surface-border">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-12 w-12 shrink-0 rounded-xl bg-surface-hover border border-surface-border overflow-hidden">
                      {order.product.image ? (
                        <img src={order.product.image} alt={order.product.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <span className="font-display text-lg text-accent/40">
                            {order.product.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <Link
                        to={`/product/${order.productId}`}
                        className="font-medium text-brand-white hover:text-accent transition-colors line-clamp-1"
                      >
                        {order.product.name}
                      </Link>
                      <p className="text-xs text-brand-muted mt-0.5">
                        {op.table.role}:{' '}
                        <span className="text-brand-white">
                          {isBuyer ? op.roles.buyer : op.roles.seller}
                        </span>
                        {' · '}
                        x{order.quantity}
                        {' · '}
                        {dateFormatter.format(new Date(order.createdAt))}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    <span className="flex flex-col items-end">
                      <span className="text-base font-bold text-accent">
                        {formatPrice(order.totalPrice, order.currency, locale)}
                      </span>
                      {converted ? (
                        <span className="text-[11px] text-brand-muted">
                          {`${op.convertedPrefix} ${converted}`}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={`badge ${
                        order.status === 'COMPLETED'
                          ? 'badge-green'
                          : order.status === 'CANCELLED'
                            ? 'bg-brand-muted/10 text-brand-muted border border-brand-muted/10'
                            : order.status === 'DISPUTED'
                              ? 'bg-brand-danger/10 text-brand-danger border border-brand-danger/10'
                              : 'badge-accent'
                      }`}
                    >
                      {op.statuses[statusKey] ?? order.status}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs text-brand-muted">
                      {deliveryStatus === 'DELIVERED' || deliveryStatus === 'CONFIRMED' ? (
                        <CheckCircle size={14} className="text-brand-success" />
                      ) : deliveryStatus === 'CANCELLED' ? (
                        <XCircle size={14} className="text-brand-danger" />
                      ) : deliveryStatus === 'DISPUTED' ? (
                        <ShieldAlert size={14} className="text-brand-danger" />
                      ) : (
                        <Truck size={14} className="text-accent" />
                      )}
                      {op.deliveryStatuses[deliveryKey] ?? deliveryStatus}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleMessages(order.id)}
                      className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-surface-border text-xs text-brand-muted hover:text-brand-white hover:border-accent/30 transition-colors"
                    >
                      <MessageSquare size={14} />
                      {op.messages.toggle}
                    </button>
                  </div>
                </div>

                {deliveredContent.length > 0 && (
                  <div className="mt-4 p-3 rounded-lg bg-surface-base border border-surface-border">
                    <p className="text-xs text-brand-muted uppercase tracking-widest mb-2">
                      {op.actions.deliveredContent}
                    </p>
                    <ul className="space-y-1">
                      {deliveredContent.map((content, idx) => (
                        <li key={idx} className="text-sm text-brand-white font-mono break-all">
                          {content}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-4 pt-3 border-t border-surface-border flex flex-wrap items-center gap-2">
                    {canConfirm && (
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => handleConfirm(order.id)}
                        className="btn-primary h-9 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {op.actions.confirm}
                      </button>
                    )}
                    {canDeliver && (
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => { setDeliverId(order.id); setDeliverText(''); }}
                        className="btn-primary h-9 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {op.actions.deliver}
                      </button>
                    )}
                    {canCancel && (
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => handleCancel(order.id)}
                        className="btn-secondary h-9 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {op.actions.cancel}
                      </button>
                    )}
                    {canDispute && (
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => { setDisputeId(order.id); setDisputeReason('not-delivered'); setDisputeDescription(''); }}
                        className="btn-secondary h-9 text-sm !text-brand-danger disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <ShieldAlert size={14} />
                        {op.dispute.report}
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => { setReportId(order.id); setReportSubmitted(null); setReportError(null); setReportReason('not-delivered'); setReportDetails(''); }}
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-brand-danger/30 text-brand-danger text-sm hover:bg-brand-danger/10 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      <Flag size={14} />
                      {op.report.button}
                    </button>
                    {isBusy && <Loader2 size={16} className="animate-spin text-accent" />}
                  </div>

                {canConfirm && order.autoReleaseAt && (
                  <p className="mt-3 flex items-center gap-1.5 text-xs text-brand-muted">
                    <Clock size={14} className="text-accent" />
                    {op.autoReleaseIn.replace('{time}', formatCountdown(order.autoReleaseAt, countdownUnits, countdownFormatter))}
                  </p>
                )}

                {disputeId === order.id && (
                  <form
                    onSubmit={(e) => { e.preventDefault(); handleSubmitDispute(order.id); }}
                    className="mt-4 space-y-3 rounded-lg border border-surface-border bg-surface-base p-4"
                  >
                    <div>
                      <label htmlFor={`dispute-reason-${order.id}`} className="block text-sm text-brand-white mb-1.5">
                        {op.dispute.reasonLabel}
                      </label>
                      <select
                        id={`dispute-reason-${order.id}`}
                        value={disputeReason}
                        onChange={(e) => setDisputeReason(e.target.value)}
                        className="w-full h-10 rounded-lg bg-surface-card border border-surface-border px-3 text-sm text-brand-white focus:outline-none focus:border-accent/30 transition-colors"
                      >
                        {DISPUTE_REASONS.map((reason) => (
                          <option key={reason} value={reason}>
                            {op.dispute.reasons[reason]}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor={`dispute-description-${order.id}`} className="block text-sm text-brand-white mb-1.5">
                        {op.dispute.descriptionLabel}
                      </label>
                      <textarea
                        id={`dispute-description-${order.id}`}
                        value={disputeDescription}
                        onChange={(e) => setDisputeDescription(e.target.value)}
                        rows={3}
                        placeholder={op.dispute.descriptionPlaceholder}
                        className="w-full rounded-lg bg-surface-card border border-surface-border px-3 py-2.5 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none focus:border-accent/30 transition-colors"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="submit"
                        disabled={isBusy || !disputeDescription.trim()}
                        className="btn-primary h-9 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {op.dispute.submit}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDisputeId(null)}
                        className="btn-secondary h-9 text-sm"
                      >
                        {op.dispute.cancel}
                      </button>
                    </div>
                  </form>
                )}

                {reportId === order.id && (
                  <form
                    onSubmit={(e) => { e.preventDefault(); handleSubmitReport(order.id); }}
                    className="mt-4 space-y-3 rounded-lg border border-surface-border bg-surface-base p-4"
                  >
                    <p className="text-sm font-semibold text-brand-white">{op.report.title}</p>
                    <div>
                      <label htmlFor={`report-reason-${order.id}`} className="block text-sm text-brand-white mb-1.5">
                        {op.report.reasonLabel}
                      </label>
                      <select
                        id={`report-reason-${order.id}`}
                        value={reportReason}
                        onChange={(e) => setReportReason(e.target.value)}
                        className="w-full h-10 rounded-lg bg-surface-card border border-surface-border px-3 text-sm text-brand-white focus:outline-none focus:border-accent/30 transition-colors"
                      >
                        {REPORT_REASONS.map((reason) => (
                          <option key={reason} value={reason}>
                            {op.report.reasons[reason]}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor={`report-details-${order.id}`} className="block text-sm text-brand-white mb-1.5">
                        {op.report.detailsLabel}
                      </label>
                      <textarea
                        id={`report-details-${order.id}`}
                        value={reportDetails}
                        onChange={(e) => setReportDetails(e.target.value)}
                        rows={3}
                        placeholder={op.report.detailsPlaceholder}
                        className="w-full rounded-lg bg-surface-card border border-surface-border px-3 py-2.5 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none focus:border-accent/30 transition-colors"
                      />
                    </div>
                    {reportError && (
                      <p className="flex items-center gap-1.5 text-xs text-brand-danger" role="alert">
                        <AlertCircle size={12} />
                        {reportError}
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        type="submit"
                        disabled={reportBusy}
                        className="btn-primary h-9 text-sm inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {reportBusy && <Loader2 size={14} className="animate-spin" />}
                        {op.report.submit}
                      </button>
                      <button
                        type="button"
                        onClick={() => setReportId(null)}
                        className="btn-secondary h-9 text-sm"
                      >
                        {op.report.cancel}
                      </button>
                    </div>
                  </form>
                )}

                {reportSubmitted?.orderId === order.id && (
                  <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-brand-success/30 bg-brand-success/10 px-3 py-2.5 text-sm text-brand-success">
                    <CheckCircle size={16} className="shrink-0 mt-0.5" />
                    <div>
                      <p>{op.report.success}</p>
                      <p className="mt-0.5 text-xs">
                        {op.report.ticketId}: <span className="font-mono font-medium">{reportSubmitted.ticketId}</span>
                      </p>
                    </div>
                  </div>
                )}

                {order.status === 'DISPUTED' && (
                  <div className="mt-4 rounded-lg border border-brand-danger/20 bg-brand-danger/5 p-4">
                    <div className="flex items-center gap-2">
                      <ShieldAlert size={16} className="text-brand-danger" />
                      <span className="text-sm font-semibold text-brand-white">{op.dispute.status}</span>
                      <span className="badge bg-brand-danger/10 text-brand-danger border border-brand-danger/10">
                        {op.statuses.DISPUTED}
                      </span>
                    </div>
                    {order.dispute?.reason && (
                      <p className="mt-2 text-sm text-brand-white">
                        {op.dispute.reasonLabel}:{' '}
                        <span className="text-brand-muted">
                          {op.dispute.reasons[order.dispute.reason as keyof typeof op.dispute.reasons] ?? order.dispute.reason}
                        </span>
                      </p>
                    )}
                    {order.dispute?.description && (
                      <p className="mt-1 text-sm text-brand-muted">{order.dispute.description}</p>
                    )}
                    {order.dispute?.sellerResponseDeadline && (
                      <p className="mt-2 flex items-center gap-1.5 text-xs text-brand-muted">
                        <Clock size={14} className="text-accent" />
                        {op.dispute.sellerDeadline}:{' '}
                        <span className="text-brand-white">
                          {dateFormatter.format(new Date(order.dispute.sellerResponseDeadline))}
                        </span>
                      </p>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {canRespond && (
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() => { setRespondId(order.id); setRespondText(''); }}
                          className="btn-primary h-9 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {op.dispute.respond}
                        </button>
                      )}
                      {canAddEvidence && (
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() => { setEvidenceId(order.id); setEvidenceNote(''); }}
                          className="btn-secondary h-9 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <FileText size={14} />
                          {op.dispute.addEvidence}
                        </button>
                      )}
                    </div>

                    {respondId === order.id && (
                      <div className="mt-3 space-y-2">
                        <textarea
                          value={respondText}
                          onChange={(e) => setRespondText(e.target.value)}
                          rows={3}
                          placeholder={op.dispute.respondPlaceholder}
                          className="w-full rounded-lg bg-surface-base border border-surface-border px-3 py-2.5 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none focus:border-accent/30 transition-colors"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={isBusy || !respondText.trim()}
                            onClick={() => handleRespond(order.id)}
                            className="btn-primary h-9 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {op.dispute.respondSubmit}
                          </button>
                          <button
                            type="button"
                            onClick={() => setRespondId(null)}
                            className="btn-secondary h-9 text-sm"
                          >
                            {op.dispute.cancel}
                          </button>
                        </div>
                      </div>
                    )}

                    {evidenceId === order.id && (
                      <div className="mt-3 space-y-2">
                        <textarea
                          value={evidenceNote}
                          onChange={(e) => setEvidenceNote(e.target.value)}
                          rows={2}
                          placeholder={op.dispute.evidencePlaceholder}
                          className="w-full rounded-lg bg-surface-base border border-surface-border px-3 py-2.5 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none focus:border-accent/30 transition-colors"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={isBusy || !evidenceNote.trim()}
                            onClick={() => handleEvidence(order.id)}
                            className="btn-primary h-9 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {op.dispute.evidenceSubmit}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEvidenceId(null)}
                            className="btn-secondary h-9 text-sm"
                          >
                            {op.dispute.cancel}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {deliverId === order.id && (
                  <div className="mt-4 space-y-2">
                    <textarea
                      value={deliverText}
                      onChange={(e) => setDeliverText(e.target.value)}
                      rows={3}
                      placeholder={op.actions.deliverPlaceholder}
                      className="w-full rounded-lg bg-surface-base border border-surface-border px-3 py-2.5 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none focus:border-accent/30 transition-colors font-mono"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleDeliver(order.id)}
                        className="btn-primary h-9 text-sm"
                      >
                        {op.actions.deliverSubmit}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDeliverId(null); setDeliverText(''); }}
                        className="btn-secondary h-9 text-sm"
                      >
                        {op.actions.cancel}
                      </button>
                    </div>
                  </div>
                )}

                {messagesId === order.id && (
                  <div className="mt-4 pt-3 border-t border-surface-border">
                    {messagesLoading ? (
                      <div className="flex items-center gap-2 py-3 text-sm text-brand-muted">
                        <Loader2 size={14} className="animate-spin text-accent" />
                        {op.messages.loading}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                          {messages.length === 0 ? (
                            <p className="text-sm text-brand-muted py-2">{op.messages.empty}</p>
                          ) : (
                            messages.map((msg) => {
                              const mine = msg.senderId === user.userId;
                              return (
                                <div key={msg.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                                  <div
                                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                                      mine
                                        ? 'bg-accent/10 border border-accent/20 text-brand-white'
                                        : 'bg-surface-base border border-surface-border text-brand-white'
                                    }`}
                                  >
                                    {!mine && (
                                      <p className="mb-0.5 text-[11px] font-medium text-accent">
                                        {msg.sender.displayName || msg.sender.username}
                                      </p>
                                    )}
                                    <p className="break-words">{msg.body}</p>
                                    <p className="mt-0.5 text-[10px] text-brand-muted text-end">
                                      {dateFormatter.format(new Date(msg.createdAt))}
                                    </p>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                        {messagesError && (
                          <p className="flex items-center gap-1.5 text-xs text-brand-danger">
                            <AlertCircle size={12} />
                            {messagesError}
                          </p>
                        )}
                        <div className="flex items-center gap-2">
                          <input
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSendMessage(order.id); }}
                            placeholder={op.messages.placeholder}
                            className="flex-1 h-9 rounded-lg bg-surface-base border border-surface-border px-3 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none focus:border-accent/30 transition-colors"
                          />
                          <button
                            type="button"
                            onClick={() => handleSendMessage(order.id)}
                            className="btn-primary h-9 px-3 text-sm"
                          >
                            <Send size={14} />
                            {op.messages.send}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
